import React, { useState, useEffect, useRef, useCallback } from 'react';

import { 
  Character, 
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SaveButton from "@components/input/buttons/SaveButton";
import RefreshButton from "@components/input/buttons/RefreshButton";
import CookieButton from "@components/input/buttons/CookieButton";
import LinkButton from "@components/input/buttons/LinkButton";
import CheckBox from "@components/input/CheckBox";

interface Props {
  character: Character;
}

const cookie_time = 30;
const save_time = 180;

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

// TODO: Make it keep previous versions
// And make it so if on load it finds missing things
// it disables auto-save, checks the last version, 
// and suggests reverting. 
// If the last one is missing things as well, 
// check the one before that. Keep 5 previous versions.

export default function CharacterSaveTools({
  character,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [auto_save, setAutoSave] = useState(false);
  const [go, setGo] = useState(false);
  const [seconds, setSeconds] = useState(save_time);
  const [cookie_seconds, setCookieSeconds] = useState(0);
  const [cookie_go, setCookieGo] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cookie_saving, setCookieSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!fetched) {
      if (!character.cookie_outdated) {
        setAutoSave(true);
        if (character.changed_since_saved) {
          setSeconds(save_time);
          setGo(true);
        }
      }
      setFetched(true);
    }
    // eslint-disable-next-line
  }, [fetched, setFetched]);

  useEffect(() => {
    if (changed) {
      setChanged(false);
      characterChanged();
    }
    // eslint-disable-next-line
  }, [changed, setChanged]);

  const characterChanged = () => {
    // console.log("characterChanged()");
    if (!cookie_go && !cookie_saving && character.changed_since_cookie_saved) {
      setCookieSeconds(cookie_time);
      setCookieGo(true);
    }
    if (auto_save && !go && !saving && character.changed_since_saved) {
      setSeconds(save_time);
      setGo(true);
    }
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (cookie_go) {
      if (cookie_seconds > 0) {
        setTimeout(() => {
          if (cookie_go) {
            setCookieSeconds(cookie_seconds - 1);
          } else {
            setCookieSeconds(0);
          }
        }, 1000);
      } else {
        setCookieSaving(true);
        character.cookie_saved = common_util.get_time_number();
        api.updateCharacter(character).then(() => {
          setCookieSaving(false);
          setCookieGo(false);
        });
      }
    }
    if (go && auto_save && !saving && character.changed_since_saved) {
      if (seconds > 0) {
        setTimeout(() => {
          if (go) {
            setSeconds(seconds - 1);
          } else {
            setSeconds(0);
          }
        }, 1000);
      } else {
        setProcessing(true);
        setSaving(true);
        api.removeCharacterCookies(character._id);
        character.last_saved = common_util.get_time_number();
        character.cookie_saved = character.last_saved;
        setCookieSeconds(0);
        setCookieSaving(true);
        api.updateCharacter(character, ["all"], true).then(() => {
          setProcessing(false);
          setCookieSaving(false);
          setSaving(false);
          setGo(false);
        });
      }
    }
  });

  const charChanged = useCallback(() => { 
    console.log("Char Save Tools - Changed");
    setTimeout(() => {
      setChanged(true);
    }, 100);
  }, []);

  useEffect(() => {
    console.log("Char Save Tools");
    character.subscription_holder.subscribe("Character", mounted, charChanged);
  }, [character, charChanged]);

  const secondsToTimeString = (s: number) => {
    const mins = Math.floor(s/60);
    const secs = s % 60;
    return mins > 0 ? (secs > 9 ? `${mins}:${secs}` : `${mins}:0${secs}`) : `${secs}`;
  }
  
  // console.log(character.last_changed);
  // console.log(character.cookie_saved);
  // console.log(character.changed_since_cookie_saved);
  // console.log(character.last_saved);
  // console.log(character.changed_since_saved);

  return (
    <div>
      { api.online ? 
        <>
          <div style={{ float: "left" }}>
            <CheckBox name="Auto save to DB" 
              value={auto_save}
              disabled={character.cookie_outdated}
              onChange={(changed: boolean) => {
                setSeconds(save_time);
                setAutoSave(changed);
                // console.log(character.changed_since_saved);
                if (changed && !saving && character.changed_since_saved) {
                  setGo(true);
                }
              }}
            />
          </div>
          <div style={{ float: "left" }}>
            <SaveButton label={ (character.changed_since_saved || character.cookie_outdated) ? (auto_save && go ? `${secondsToTimeString(seconds)}` : "Save to DB") : "DB Up to Date" } 
              disabled={ processing || (!character.changed_since_saved && !character.cookie_outdated) }
              onClick={() => {
                setProcessing(true);
                setSaving(true);
                api.removeCharacterCookies(character._id);
                character.last_saved = common_util.get_time_number();
                character.cookie_saved = character.last_saved;
                setCookieSeconds(0);
                setSeconds(0);
                setCookieSaving(true);
                api.updateCharacter(character, ["all"], true).then(() => {
                  setProcessing(false);
                  setCookieSaving(false);
                  setSaving(false);
                  setGo(false);
                });
              }}
            />
          </div>
          <div style={{ float: "left" }}>
            <RefreshButton label="Reload from DB" 
              disabled={processing || cookie_saving || saving}
              onClick={() => {
                setProcessing(true);
                setCookieGo(false);
                setCookieSeconds(0);
                setSeconds(0);
                const id = character._id;
                // debugger;
                api.removeCharacterCookies(id);
                character.subscription_holder.reload();
              }}
            />
          </div>
        </>
      :
        <>
          <div style={{ float: "left" }}>
            <SaveButton label="DB Unavailable" 
              disabled
              onClick={() => {}}
            />
          </div>
        </>
      }
      <div style={{ float: "left" }}>
        <CookieButton label={ cookie_saving || saving ? "Saving" : (cookie_seconds === 0 || !cookie_go ? "Saved" : `Auto save to Cookie in ${cookie_seconds}`) } 
          disabled={processing || !cookie_go}
          onClick={() => {
            // debugger;
            setCookieGo(false);
            setCookieSeconds(0);
            setCookieSaving(true);
            character.cookie_saved = common_util.get_time_number();
            api.updateCharacter(character).then(() => {
              setCookieSaving(false);
              setCookieGo(false);
            });
          }}
        />
      </div>
      <div style={{ float: "left" }}>
        <LinkButton 
          label={ character.changed_since_cookie_saved ? "Printable PDF (Save First)" : "Printable PDF"}
          disabled={processing || character.changed_since_cookie_saved}
          to={`/dandd/character/printable/${character._id}`} 
          target="_blank" 
        />
      </div>
      { character.cookie_outdated &&
        <div style={{ float: "left", color: "red" }} className="header">
          Cookie Outdated 
          { api.true_user && api.true_user.is_admin && <>{` ${character.last_saved} > ${character.cookie_saved}`}</> }
        </div>
      }
    </div>
  );
}
